import { usePageTitle } from 'corso-admin-ui';
import { PrimaryEntity, Sidekick } from 'corso-types';
import { useMemo } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { z } from 'zod';
import { claimParamsSchema } from '~/features/claimReview/claimParamsSchema';

// TODO: make this more typesafe!
const recordId = z.coerce.number().int().positive();

// most specific first: `claims/shipping/:id` would otherwise read as a claim of type "shipping"
function toPageContext(pathname: string) {
  const shippingClaim = matchPath(
    '/:storeId/claims/shipping/:shippingClaimId',
    pathname,
  );
  if (shippingClaim) {
    const id = recordId.safeParse(shippingClaim.params.shippingClaimId);
    if (id.success) {
      return { kind: PrimaryEntity.Shipping, id: id.data };
    }
  }

  const claim = matchPath('/:storeId/claims/:claimType/:claimId', pathname);
  if (claim) {
    const id = recordId.safeParse(claim.params.claimId);
    const params = claimParamsSchema.safeParse(claim.params);
    if (id.success && params.success) {
      return { kind: params.data.claimType, id: id.data };
    }
  }

  const registration = matchPath(
    '/:storeId/registrations/:registrationId',
    pathname,
  );
  if (registration) {
    const id = recordId.safeParse(registration.params.registrationId);
    if (id.success) {
      return { kind: PrimaryEntity.Registration, id: id.data };
    }
  }

  const order = matchPath(
    '/:storeId/orders/lookup/:orderIdFromPlatform',
    pathname,
  );
  if (order?.params.orderIdFromPlatform) {
    return {
      kind: PrimaryEntity.Order,
      sourceOrderId: order.params.orderIdFromPlatform,
    };
  }

  const automation = matchPath(
    '/:storeId/settings/automations/edit/:storeRuleId',
    pathname,
  );
  if (automation) {
    const id = recordId.safeParse(automation.params.storeRuleId);
    if (id.success) {
      return { kind: 'Automation' as const, id: id.data };
    }
  }

  const settings = matchPath('/:storeId/settings/:section/*', pathname);
  if (settings?.params.section) {
    return { kind: 'Settings' as const, section: settings.params.section };
  }

  return null;
}

/** What the merchant has open, for Sidekick. */
export function usePageContext() {
  const { pathname } = useLocation();
  const title = usePageTitle() ?? '';
  const page = useMemo(() => toPageContext(pathname), [pathname]);
  return useMemo(
    () => page && ({ ...page, title } satisfies Sidekick.PageContext),
    [page, title],
  );
}
